import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { AlertCircle, CheckCircle2, Truck, Info } from 'lucide-react';

/**
 * Sessão 32 — QW5: Supplier MOQ Banner for shopping cart.
 *
 * Surfaces "MOQ atingido vs não atingido" + free-freight progress
 * per supplier, BEFORE the operator clicks "Create orders".
 *
 * Per AI_ROLE_CHARTER.md §1 — assistente sénior lembra-te de MOQs
 * para não teres de o fazer.
 *
 * Props:
 *   supplierSummary: { [supplier_name]: {
 *     count, total, moq_value?, free_freight_threshold?,
 *     items: [{sku, quantity, moq, unit_price}]
 *   } }
 *   testid: optional prefix
 */
export const SupplierMOQBanner = ({ supplierSummary = {}, testid = 'moq' }) => {
  const suppliers = Object.entries(supplierSummary);
  if (suppliers.length === 0) return null;

  const analyze = (data) => {
    const total = data.total || 0;
    const items = data.items || [];

    // Per-supplier MOQ value: use first available value or 0 (not configured)
    const moqValue = data.moq_value || data.supplier_moq || 0;
    const freeFreight = data.free_freight_threshold || data.free_freight || 0;

    // Per-line MOQ checks
    const lineMoqIssues = items.filter(
      (it) => it.moq && it.quantity > 0 && it.quantity < it.moq
    );

    const moqValueHit = !moqValue || total >= moqValue;
    const freeFreightHit = !freeFreight || total >= freeFreight;
    const freeFreightGap = freeFreight && total < freeFreight ? freeFreight - total : 0;

    const hasIssues = !moqValueHit || lineMoqIssues.length > 0;
    const hasWarnings = !freeFreightHit && !!freeFreight;

    return {
      total,
      moqValue,
      freeFreight,
      lineMoqIssues,
      moqValueHit,
      freeFreightHit,
      freeFreightGap,
      hasIssues,
      hasWarnings,
    };
  };

  return (
    <Card className="border border-slate-200 mb-4" data-testid={`${testid}-banner-card`}>
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-center gap-2 mb-3">
          <Truck className="h-4 w-4 text-slate-600" />
          <p className="text-sm font-semibold text-slate-700">
            Resumo por fornecedor — verificação MOQ e frete
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {suppliers.map(([supplier, data]) => {
            const a = analyze(data);
            const statusColor = a.hasIssues
              ? 'border-orange-200 bg-orange-50/50'
              : a.hasWarnings
                ? 'border-amber-200 bg-amber-50/50'
                : 'border-emerald-200 bg-emerald-50/50';
            const statusIcon = a.hasIssues ? AlertCircle : a.hasWarnings ? Info : CheckCircle2;
            const Icon = statusIcon;
            const iconColor = a.hasIssues
              ? 'text-orange-600'
              : a.hasWarnings
                ? 'text-amber-600'
                : 'text-emerald-600';

            return (
              <div
                key={supplier}
                className={`rounded border ${statusColor} p-2.5`}
                data-testid={`${testid}-supplier-${supplier.replace(/\s+/g, '-')}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${iconColor}`} />
                      <p className="text-sm font-medium text-slate-800 truncate">{supplier}</p>
                    </div>
                    <p className="text-xs text-slate-600 mt-0.5">
                      {data.count} itens · €{a.total.toFixed(2)}
                    </p>
                  </div>
                </div>

                {/* MOQ value check */}
                {a.moqValue > 0 && (
                  <div className="mt-2 text-xs">
                    {a.moqValueHit ? (
                      <span className="text-emerald-700">
                        ✓ MOQ €{a.moqValue} atingido
                      </span>
                    ) : (
                      <span className="text-orange-700">
                        ⚠️ MOQ €{a.moqValue} — faltam €{(a.moqValue - a.total).toFixed(2)}
                      </span>
                    )}
                  </div>
                )}

                {/* Free freight check */}
                {a.freeFreight > 0 && (
                  <div className="mt-1 text-xs">
                    {a.freeFreightHit ? (
                      <span className="text-emerald-700">✓ Frete grátis (€{a.freeFreight})</span>
                    ) : (
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-amber-700 cursor-help">
                              Frete grátis em €{a.freeFreight} — faltam €{a.freeFreightGap.toFixed(2)}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="bg-white border border-slate-200 max-w-xs">
                            <p className="text-xs text-slate-700">
                              Adicionar mais SKUs deste fornecedor pode evitar o custo de
                              frete. Recomendado se o sub-total estiver perto do limite.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                )}

                {/* Per-line MOQ issues */}
                {a.lineMoqIssues.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-orange-100">
                    <p className="text-[11px] text-orange-700 font-medium mb-0.5">
                      {a.lineMoqIssues.length} linha(s) abaixo do MOQ por SKU:
                    </p>
                    <ul className="text-[11px] text-orange-700 space-y-0.5">
                      {a.lineMoqIssues.slice(0, 3).map((it) => (
                        <li key={it.sku} className="truncate">
                          • {it.sku}: {it.quantity}/{it.moq}
                        </li>
                      ))}
                      {a.lineMoqIssues.length > 3 && (
                        <li className="italic">… e mais {a.lineMoqIssues.length - 3}</li>
                      )}
                    </ul>
                  </div>
                )}

                {/* No MOQ config */}
                {!a.moqValue && !a.freeFreight && a.lineMoqIssues.length === 0 && (
                  <Badge
                    variant="outline"
                    className="mt-2 h-5 px-1.5 text-[10px] font-normal text-slate-500 border-slate-300"
                  >
                    Sem MOQ configurado
                  </Badge>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default SupplierMOQBanner;
